import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { shouldUseBulk } from '@/hooks/useBulkAction/useBulkAction';
import { BulkSelectionState } from '@/hooks/useBulkSelection/types';
import { queryClient } from '@/queryclient';
import { RemoveTag } from '@/sections/RemoveTag';
import { useMutation } from '@/utils/api';
import { tagRemovedError, tagRemovedSuccess } from '@/utils/tagToastMessages';

interface Props {
  open: boolean;
  onClose: () => void;
  /**
   * Keys of assets to remove tag from.
   *
   * INVARIANT: assetKeys MUST be derived from the SAME source as selectionState.
   * Callers either:
   *   (a) pass neither selectionState nor onBulkRequested (per-item modal), OR
   *   (b) pass selectionState; assetKeys should be selectionState.keys or equivalent.
   * NEVER pass selectionState with N=1000 keys alongside assetKeys=[single].
   */
  assetKeys: string[];
  onSuccess?: () => void;
  initialTag?: string;
  /** Bulk selection state from caller. When provided AND shouldUseBulk returns true,
   * the modal delegates to onBulkRequested instead of per-item PUTs. */
  selectionState?: BulkSelectionState;
  /** Called when bulk path is triggered; caller handles BulkConfirmationDialog. */
  onBulkRequested?: (tag: string) => void;
}

/**
 * AssetRemoveTag Component
 *
 * Manages the removal of tags from multiple assets in bulk.
 * Follows the same pattern as AssetTag component for consistency.
 *
 * Features:
 * - Bulk tag removal across multiple assets
 * - Fetches common tags across all selected assets
 * - Predicate-based query invalidation for real-time updates
 * - Consistent error handling with toast notifications
 *
 * @example
 * ```tsx
 * <AssetRemoveTag
 *   open={isRemoveTagModalOpen}
 *   onClose={() => setIsRemoveTagModalOpen(false)}
 *   assetKeys={selectedAssets.map(asset => asset.key)}
 * />
 * ```
 */
export const AssetRemoveTag = ({
  open,
  onClose,
  assetKeys,
  onSuccess,
  initialTag,
  selectionState,
  onBulkRequested,
}: Props) => {
  const axios = useAxios();

  const { mutateAsync: removeTag, status: removingTag } = useMutation({
    defaultErrorMessage: 'Failed to remove tag',
    mutationFn: async (tagToRemove: string) => {
      const promises = assetKeys.map(async assetKey => {
        // Fetch current asset tags
        const currentAsset = await axios.get(`/my`, {
          params: {
            label: 'asset',
            key: assetKey,
            exact: true,
          },
        });

        const asset = currentAsset.data.assets?.[0];
        const existingTags = asset?.tags || [];
        const currentStatus = asset?.status || 'A';

        // Remove the tag
        const updatedTags = existingTags.filter(
          (tag: string) => tag !== tagToRemove
        );

        const payload = {
          key: assetKey,
          status: currentStatus, // Include current status (required by backend)
          tags: updatedTags,
        };

        return axios.put('/asset', payload);
      });

      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled(promises);

      // Count successes and failures
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      // Invalidate cache if any operations succeeded
      if (succeeded > 0) {
        // Use predicate-based invalidation to catch ALL asset queries
        // This matches the pattern from AssetTag and useUpdateSubscription
        // NOTE: UniqueQueryKeys.MY is 'MY' (uppercase), not 'my'
        await queryClient.invalidateQueries({
          predicate: query => {
            const queryKey = query.queryKey as string[];
            // Invalidate any 'MY' query for 'asset' resource
            return queryKey[0] === 'MY' && queryKey[1] === 'asset';
          },
          refetchType: 'all', // Use 'all' to force refetch of drawer and table
        });
      }

      // Log failures for debugging with enhanced error details
      if (failed > 0) {
        const failedResults = results
          .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
          .map((r, i) => ({
            assetKey: assetKeys[i],
            error: r.reason,
            errorResponse: r.reason?.response,
            errorData: r.reason?.response?.data,
          }));
        console.error(
          '[AssetRemoveTag] Failed to remove tag from assets:',
          failedResults
        );
      }

      // Return result summary for handleSubmit to use
      return { succeeded, failed, tagToRemove };
    },
  });

  const handleSubmit = async (tag: string) => {
    // Dev-mode divergence guard (R4 regression prevention)
    if (
      import.meta.env.DEV &&
      selectionState &&
      selectionState.mode === 'manual' &&
      assetKeys.length > 0 &&
      selectionState.keys.length !== assetKeys.length
    ) {
      console.warn(
        '[AssetRemoveTag] selectionState.keys.length diverges from assetKeys.length — ' +
          'likely a caller bug. Bulk path will operate on selectionState.'
      );
    }

    // Bulk path: delegate to caller's BulkConfirmationDialog
    if (selectionState && shouldUseBulk(selectionState) && onBulkRequested) {
      onBulkRequested(tag);
      onClose();
      return;
    }

    try {
      const result = await removeTag(tag);

      // Show accurate feedback based on results
      if (result.succeeded > 0 && result.failed === 0) {
        const count = result.succeeded > 1 ? result.succeeded : undefined;
        toast.success(tagRemovedSuccess('asset', count));
      } else if (result.succeeded > 0 && result.failed > 0) {
        toast.warning(
          `Tag "${result.tagToRemove}" removed from ${result.succeeded} asset${result.succeeded > 1 ? 's' : ''}, ` +
            `but failed for ${result.failed} asset${result.failed > 1 ? 's' : ''}`
        );
      } else {
        toast.error(
          `Failed to remove tag "${result.tagToRemove}" from ${result.failed} asset${result.failed > 1 ? 's' : ''}`
        );
      }

      onClose();
      onSuccess?.();
    } catch (error: unknown) {
      console.error('[AssetRemoveTag] Tag removal failed:', error);
      const err = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      toast.error(
        tagRemovedError('asset', err.response?.data?.message || err.message)
      );
    }
  };

  return (
    <RemoveTag
      open={open}
      onClose={onClose}
      disabled={removingTag === 'pending'}
      onSubmit={handleSubmit}
      assetKeys={assetKeys}
      initialTag={initialTag}
    />
  );
};
